import { useCountUp } from '@/hooks/useCountUp';
import { useLocale, useT } from '@/i18n/react';
import { formatArea, formatNumber } from '@/lib/format';
import type { Rival } from '@/lib/api';
import './rivalScore.css';

/**
 * Egy rivális mérlege: összecsapások, terület és cellamérleg.
 *
 * A fő szám a kicserélt mezők száma — ez a rangsor alapja is. Utána kisebb
 * betűvel, zölddel a szerzett, pirossal a vesztett (Geri, 2026-08-22).
 *
 * ⚠️ MEZŐ, NEM km². Az itteni számok jellemzően néhány tucat mezőt jelentenek,
 * ami km²-ben „0,000"-ként jelenne meg (egy hexagon 307 m², a `formatArea`
 * három tizedese ezer m²-es felbontás). A mérleg pont az apró különbségekről
 * szól, ezért itt a mezőszám a beszédes mérték — a km² a küldetés-kártyákon és
 * a profilon marad, ahol nagyságrendekről van szó.
 *
 * ⚠️ A `+` ÉS `−` JEL NEM DÍSZ. A két szám külön-külön is olvasható színek
 * nélkül — színvakon és fekete-fehér képernyőn is eldönthető, melyik a
 * szerzett és melyik a vesztett.
 *
 * ⚠️ A `countUp` A HÍVÓ DÖNTÉSE, és alapból kikapcsolt. A `RivalRow` akkor
 * kapcsolja be, amikor a sor láthatóvá válik — így a pörgetés és a CSS-sávok
 * együtt indulnak. Aki csak a számokat mutatja sáv nélkül, hagyja kikapcsolva:
 * önmagában pörgő szám egy listában nyugtalanság, nem kiemelés.
 */
export function RivalScore({ rival, countUp = false }: { rival: Rival; countUp?: boolean }) {
  const t = useT();
  const locale = useLocale();
  const summaryCount = (value: number) => locale === 'hu' ? String(value) : formatNumber(value);
  const encounters = rival.gainedEvents + rival.lostEvents;
  const shown = useCountUp({ enabled: countUp, duration: 720, delay: 380 });

  return (
    <span className="rival-score">
      {/*
        A látható számok `aria-hidden`-ek, mert alattuk a `__sr` szöveg
        ugyanezt mondja el összefüggő mondatban. Pörgetés közben ez már nem
        csak ismétlés lenne: a képernyőolvasó a köztes értékeket is elérhetné.
      */}
      <span className="rival-score__total" aria-hidden="true">
        {formatNumber(shown(encounters))}×
      </span>
      <span className="rival-score__area" aria-hidden="true">
        {formatArea(rival.exchangedM2)}
      </span>
      <span className="rival-score__split" aria-hidden="true">
        <span className="rival-score__gained">
          {signedCells('+', rival.gainedCells, shown(rival.gainedCells))}
          <CellHexIcon />
        </span>
        <span className="rival-score__lost">
          {signedCells('−', rival.lostCells, shown(rival.lostCells))}
          <CellHexIcon />
        </span>
      </span>
      <span className="rival-score__sr">
        {t.remainingUI.rivalSummary(summaryCount(encounters), formatArea(rival.exchangedM2), summaryCount(rival.gainedCells), summaryCount(rival.lostCells))}
      </span>
    </span>
  );
}

/**
 * Előjeles cellaszám — de NULLA ELŐJEL NÉLKÜL (Geri, GRUNDO #62): a „+0" és a
 * „−0" azt sugallta, hogy volt valami, csak kerekítve eltűnt. Az előjelet a
 * VÉGSŐ érték dönti el, nem a pörgetés közbeni: különben a számláló „0"-ról
 * „+1"-re ugrana, és a jel villanna.
 */
export function signedCells(sign: '+' | '−', value: number, shown: number = value): string {
  return value > 0 ? `${sign}${formatNumber(shown)}` : '0';
}

/**
 * Cella-jel a számok végén — csak szegély, `currentColor`, a számmal egy magas
 * (`.rival-row__hex`, `em`-ben méretezve). A sáv pirulái és a magyarázó lap
 * ugyanezt használja, hogy a kettő egymásra ismerjen.
 */
export function CellHexIcon() {
  return (
    <svg
      className="rival-row__hex"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 2.5 8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5L12 2.5Z" />
    </svg>
  );
}
